import { Injectable, inject } from "@angular/core";
import { ToastMessageType, ToastMessage } from '@k12/models';
import { BehaviorSubject } from "rxjs";
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';

@Injectable({
	providedIn: 'root'
})
export class ToastMessageService {
	public showToast$ = new BehaviorSubject<ToastMessage[]>([]);

	private counter: number = 0;
	private readonly interval: number = 5000;
	private toasts: ToastMessage[] = [];
	private overlayRef: OverlayRef | null = null;
	private overlayCreationPromise: Promise<void> | null = null;
	
	private readonly overlay = inject(Overlay);

	private createToastOverlay(): Promise<void> {
		if (this.overlayRef) {
			return Promise.resolve();
		}
		
		if (this.overlayCreationPromise) {
			return this.overlayCreationPromise;
		}
		
		this.overlayCreationPromise = (async () => {
			const positionStrategy = this.overlay.position()
				.global()
				.top('200px')
				.centerHorizontally();

			this.overlayRef = this.overlay.create({
				positionStrategy,
				panelClass: 'toast-overlay-pane',
				hasBackdrop: false
			});

			// Dynamically import and create ToastComponent
			const module = await import('@k12/app-components');
			const portal = new ComponentPortal(module.ToastComponent);
			this.overlayRef.attach(portal);
			this.overlayCreationPromise = null;
		})();
		
		return this.overlayCreationPromise;
	}

	private destroyToastOverlay(): void {
		if (this.overlayRef) {
			this.overlayRef.dispose();
			this.overlayRef = null;
		}
	}

	public pauseToast(toast: ToastMessage): void {
		if (!toast.persist && toast.timeoutId) {
			clearTimeout(toast.timeoutId);
			toast.timeoutId = null;
			toast.pause = true;
			const remaining = Date.now() - (toast.start ?? 0);
			toast.elapsed -= remaining;
		}
	}

	public removeToast(id: number): void {
		const index = this.toasts.findIndex(toast => toast.id === id);
		if (index === -1) {
			return;
		}

		const toast = this.toasts[index];
		if (toast.persist) {
			this.toasts.splice(index, 1);
		} else {
			this.toasts = this.toasts.filter(t => t.id !== id);
		}

		this.updateToasts();
		
		// Destroy overlay if no toasts remain
		if (this.toasts.length === 0) {
			this.destroyToastOverlay();
		}
	}

	public resumeToast(toast: ToastMessage): void {
		if (toast.pause) {
			toast.pause = false;
			this.startTimer(toast);
		}
	}

	public showToast(type: ToastMessageType, message: string) {
		// Create toast immediately
		const out = this.counter;
		const toast: ToastMessage = {
			id: this.counter++,
			message: message,
			type: type,
			duration: this.interval,
			elapsed: this.interval,
			pause: false,
			persist: false,
			dismiss: true
		}
		this.toasts.push(toast);
		
		// Create overlay and update toasts (async but non-blocking)
		if (this.toasts.length === 1) {
			this.createToastOverlay().then(() => {
				this.updateToasts();
				this.startTimer(toast);
			});
		} else {
			this.updateToasts();
			this.startTimer(toast);
		}
		
		return this.toasts[out];
	}

	private startTimer(toast: ToastMessage): void {
		toast.start = Date.now();
		toast.timeoutId = setTimeout(() => {
			if (!toast.persist) {
				this.removeToast(toast.id);
			}
		}, toast.elapsed);
	}

	public setPersist(toast: ToastMessage, persist: boolean) {
		this.toasts[toast.id].persist = persist;
	}

	public setDismissable(toast: ToastMessage, dismiss: boolean) {
		this.toasts[toast.id].dismiss = dismiss;
	}

	public updateToast(id: number, toast: ToastMessage) {
		if (toast) {
			this.toasts[id] = toast;
		}
	}

	private updateToasts(): void {
		const toastList = this.toasts.slice(-3);
		this.showToast$.next(toastList);
	}
}
